import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Slide,
  Box,
  Card,
  CardContent,
  Button,
  Stack,
  LinearProgress,
  Chip,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemText,
  Alert,
  Tooltip,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import useStore from '../store/useStore';
import { translations } from '../translations';
import AssessmentVideoRecorder from './AssessmentVideoRecorder';
import PendingClipsPanel from './video/PendingClipsPanel';
import StreakFx, { StreakBadge } from './fx/StreakFx';
import useClipQueue from './video/useClipQueue';
import useLiveRecording from './video/useLiveRecording';
import LiveRecordingPill from './video/LiveRecordingPill';
import { inNativeApp } from './video/nativeApp';
import { canRecordInBrowser } from './video/videoApi';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const STORAGE_KEY = 'speedModeState';

function getStoredState(profileId) {
  try {
    const data = JSON.parse(localStorage.getItem(STORAGE_KEY));
    if (data && data.profileId === profileId) return data;
  } catch (e) {
    /* ignore */
  }
  return null;
}

function storeState(profileId, index, selectedThemes, items) {
  // Persist scores keyed by itemId (or tIdx-iIdx fallback) so they survive resume
  const scores = {};
  if (items) {
    items.forEach((item) => {
      const key = item.itemId || `${item.tIdx}-${item.iIdx}`;
      scores[key] = item.score;
    });
  }
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({
      profileId,
      lastIndex: index,
      selectedThemes,
      scores,
      savedAt: Date.now(),
    })
  );
}

function clearStoredState() {
  localStorage.removeItem(STORAGE_KEY);
}

export default function FlashcardAssessment({
  open,
  onClose,
  profile,
  profileId,
  initialThemeFilter,
  sessionTitle,
  onSave,
  // Bulk (group) session: { assessed, total, isLast }. Saving then advances to
  // the next student instead of closing; closing ends the whole session.
  bulkSession,
  onSkipStudent,
}) {
  const [phase, setPhase] = useState('select'); // 'select' | 'assess' | 'summary'
  const [selectedThemes, setSelectedThemes] = useState([]);
  const [items, setItems] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [streak, setStreak] = useState(0);
  const [maxStreak, setMaxStreak] = useState(0);
  const [saving, setSaving] = useState(false);
  const [cheatSheetOpen, setCheatSheetOpen] = useState(false);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [wordImages, setWordImages] = useState({}); // word (lowercase) → imageUrl
  const [actionError, setActionError] = useState(null); // { action, message, opts }
  const [filterFallback, setFilterFallback] = useState(false);
  const touchStartRef = useRef(null);
  const autoSaveCountRef = useRef(0);
  const AUTO_SAVE_EVERY = 5;
  // Refs mirror index/items/phase so rapid key presses never act on a stale card
  const indexRef = useRef(0);
  const itemsRef = useRef([]);
  const phaseRef = useRef('select');
  const keyHandlersRef = useRef({});
  // Themes as they were when the session opened — restored on discard
  const snapshotRef = useRef(null);
  const autoSavedRef = useRef(false);
  const autoSavePromiseRef = useRef(null);

  const { language } = useStore();
  const t = translations[language];

  // Video clips recorded during the session. Each is uploaded to the profile
  // it was recorded for; with no profile id yet it waits in memory for one.
  const clipQueue = useClipQueue(profileId);
  const [clipsDialogOpen, setClipsDialogOpen] = useState(false);
  const [videoWarn, setVideoWarn] = useState('');
  const recBusyRef = useRef(false);
  const recPanelOpenRef = useRef(false);
  const recProfileRef = useRef(null);
  const leaveWarnedRef = useRef(false);
  const handleRecBusy = useCallback((busy) => {
    recBusyRef.current = busy;
    if (!busy) setVideoWarn('');
  }, []);
  const handleRecPanel = useCallback((isOpen) => {
    recPanelOpenRef.current = isOpen;
  }, []);
  const sessionClips = clipQueue.clips.filter(
    (c) => !c.profileId || c.profileId === profileId || c.status !== 'done'
  );
  const clipsUnsent = sessionClips.some((c) => c.status !== 'done');
  const clipsFailed = sessionClips.some((c) => c.status === 'error');
  useEffect(() => {
    if (!clipsUnsent) leaveWarnedRef.current = false;
  }, [clipsUnsent]);

  // A recording in progress (or a clip not yet kept) would be lost on exit
  const blockedByRecording = () => {
    if (!recBusyRef.current) return false;
    setVideoWarn(
      language === 'en'
        ? 'A video is being recorded. Stop the recording and keep or delete the clip first.'
        : 'Идёт запись видео. Сначала остановите её и сохраните или удалите клип.'
    );
    return true;
  };
  // Clips still uploading are lost if the page goes away: warn once
  const blockedByUnsentClips = () => {
    if (!clipsUnsent || leaveWarnedRef.current) return false;
    leaveWarnedRef.current = true;
    setVideoWarn(
      language === 'en'
        ? 'Some video clips are not uploaded yet. Wait for the upload or retry it; press again to leave anyway (the clips will be lost).'
        : 'Не все видеоклипы загружены. Дождитесь загрузки или повторите её; нажмите ещё раз, чтобы выйти всё равно (клипы пропадут).'
    );
    return true;
  };

  // Whole-assessment recording: starts with the assessment, ends with it and
  // is saved to the assessment (on by default; remembered per device).
  // in the Android app the app records (screen + face + microphone) — no browser camera needed
  const canLive = inNativeApp() || canRecordInBrowser();
  const [autoRecord, setAutoRecordState] = useState(() => {
    try {
      return canLive && localStorage.getItem('cp.autoRecord') !== '0';
    } catch (e) {
      return canLive;
    }
  });
  const setAutoRecord = (on) => {
    setAutoRecordState(on);
    try {
      localStorage.setItem('cp.autoRecord', on ? '1' : '0');
    } catch (e) {
      /* private mode */
    }
  };
  const [camFacing, setCamFacingState] = useState(() => {
    try {
      return localStorage.getItem('cp.recordFacing') || 'user';
    } catch (e) {
      return 'user';
    }
  });
  const setCamFacing = (f) => {
    setCamFacingState(f);
    try {
      localStorage.setItem('cp.recordFacing', f);
    } catch (e) {
      /* private mode */
    }
  };
  const live = useLiveRecording({ en: language === 'en' });
  const liveForRef = useRef(null); // assessment the current/last recording belongs to
  const liveStoppedRef = useRef(null); // assessment where the teacher stopped it by hand

  const profileReady = !!profile && Array.isArray(profile.themes);

  const updateItems = (next) => {
    itemsRef.current = next;
    setItems(next);
  };
  const goToIndex = (idx) => {
    indexRef.current = idx;
    setCurrentIndex(idx);
  };
  const changePhase = (next) => {
    phaseRef.current = next;
    setPhase(next);
  };

  // Load word image dictionary (sessionStorage cache, 5-min TTL)
  const WORD_IMG_KEY = 'wordImageCache';
  const WORD_IMG_TTL = 5 * 60 * 1000;
  useEffect(() => {
    if (!open) return;
    try {
      const cached = JSON.parse(sessionStorage.getItem(WORD_IMG_KEY));
      if (cached && Date.now() - cached.ts < WORD_IMG_TTL) {
        setWordImages(cached.map);
        return;
      }
    } catch (_) {}
    fetch('/api/word-images', { credentials: 'include' })
      .then((r) => r.json())
      .then((data) => {
        if (!Array.isArray(data)) return;
        const map = {};
        data.forEach(({ word, meaning, imageUrl }) => {
          const m = (meaning || '').toLowerCase().trim();
          map[m ? `${word}|${m}` : word] = imageUrl;
        });
        setWordImages(map);
        try {
          sessionStorage.setItem(
            WORD_IMG_KEY,
            JSON.stringify({ map, ts: Date.now() })
          );
        } catch (_) {}
      })
      .catch(() => {});
  }, [open]);

  // Initialize on open
  useEffect(() => {
    if (!open) return;

    snapshotRef.current = Array.isArray(profile?.themes)
      ? { profileId, themes: JSON.parse(JSON.stringify(profile.themes)) }
      : null;
    autoSavedRef.current = false;
    autoSavePromiseRef.current = null;
    autoSaveCountRef.current = 0;
    setActionError(null);
    setFilterFallback(false);
    if (!snapshotRef.current) return;

    const allThemeNames = profile.themes.map((t) => t.themeName);
    // A requested theme filter that matches nothing in this profile falls
    // back to all of its themes instead of showing a blank screen
    const resolveThemes = (wanted) => {
      if (!wanted || wanted.length === 0) return allThemeNames;
      if (buildItems(profile, wanted).length > 0) return wanted;
      setFilterFallback(true);
      return allThemeNames;
    };

    // Check for resume state
    const stored = getStoredState(profileId);
    if (stored && stored.lastIndex > 0) {
      const shouldResume = window.confirm(
        language === 'en'
          ? `Resume from item ${stored.lastIndex + 1}? (Cancel to start over)`
          : `Продолжить с элемента ${stored.lastIndex + 1}? (Отмена - начать заново)`
      );
      if (shouldResume) {
        const themes = resolveThemes(stored.selectedThemes);
        const restored = buildItems(profile, themes, stored.scores || {});
        setSelectedThemes(themes);
        // Restore both position AND previously-scored items
        updateItems(restored);
        goToIndex(Math.min(stored.lastIndex, Math.max(restored.length - 1, 0)));
        changePhase('assess');
        return;
      }
    }

    // If initialThemeFilter is provided, skip selection
    if (initialThemeFilter && initialThemeFilter.length > 0) {
      const themes = resolveThemes(initialThemeFilter);
      setSelectedThemes(themes);
      flattenAndSetItems(profile, themes);
      changePhase('assess');
    } else {
      setSelectedThemes(allThemeNames);
      updateItems([]);
      changePhase('select');
    }
    goToIndex(0);
    setStreak(0);
    setMaxStreak(0);
  }, [open, profile, profileId, initialThemeFilter]);

  // Recording follows the assessment: on when the cards start…
  useEffect(() => {
    if (
      !open ||
      phase !== 'assess' ||
      !autoRecord ||
      !profileId ||
      !items.length
    )
      return;
    if (live.active || liveStoppedRef.current === profileId) return;
    if (liveForRef.current === profileId && live.status === 'error') return;
    liveForRef.current = profileId;
    const day = new Date().toLocaleDateString(
      language === 'en' ? 'en-GB' : 'ru-RU'
    );
    live.start({
      profileId,
      facing: camFacing,
      title:
        `${language === 'en' ? 'Assessment' : 'Проверка'} ${day}${selectedThemes.length ? ` · ${selectedThemes.join(', ')}` : ''}`.slice(
          0,
          180
        ),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    phase,
    autoRecord,
    profileId,
    items.length,
    live.active,
    live.status,
  ]);
  // …and is saved when they end (summary) or the dialog goes away
  useEffect(() => {
    if ((phase === 'summary' || !open) && live.active) live.finish();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, open]);

  const stopLiveNow = () => {
    liveStoppedRef.current = profileId;
    live.finish();
  };
  const enableLive = () => {
    setAutoRecord(true);
    liveStoppedRef.current = null;
    liveForRef.current = null;
  };

  function buildItems(prof, themeNames, storedScores) {
    const flat = [];
    prof.themes.forEach((theme, tIdx) => {
      if (!themeNames.includes(theme.themeName)) return;
      (theme.items || []).forEach((item, iIdx) => {
        const key = item.itemId || `${tIdx}-${iIdx}`;
        const restoredScore =
          storedScores && storedScores[key] !== undefined
            ? storedScores[key]
            : item.score;
        flat.push({
          ...item,
          tIdx,
          iIdx,
          themeName: theme.themeName,
          originalScore: item.score,
          score: restoredScore,
        });
      });
    });
    return flat;
  }

  function flattenAndSetItems(prof, themeNames, storedScores) {
    updateItems(buildItems(prof, themeNames, storedScores));
  }

  function startAssessment() {
    flattenAndSetItems(profile, selectedThemes);
    goToIndex(0);
    setStreak(0);
    setMaxStreak(0);
    autoSaveCountRef.current = 0;
    changePhase('assess');
  }

  // Profile themes with the session's scores applied
  const buildThemesWithScores = (scoredItems) => {
    const newThemes = JSON.parse(JSON.stringify(profile.themes));
    scoredItems.forEach((item) => {
      const target = newThemes[item.tIdx]?.items?.[item.iIdx];
      if (target) target.score = item.score;
    });
    return newThemes;
  };

  const patchThemes = async (themes) => {
    const res = await fetch(`/api/profiles/${profileId}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ themes }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(
        data.message ||
          (language === 'en'
            ? 'Failed to save to database'
            : 'Ошибка сохранения')
      );
    }
  };

  // fetch() rejects with a TypeError when the request never reached the server
  const errorText = (err) =>
    err instanceof TypeError
      ? language === 'en'
        ? 'Network error'
        : 'Ошибка сети'
      : err.message;

  // Auto-saves are chained so they reach the server in order, and a final
  // save / discard can wait for the last one to land
  const waitForAutoSave = () => autoSavePromiseRef.current || Promise.resolve();

  const serverAutoSave = (updatedItems) => {
    if (!profileId || !profileReady) return;
    const newThemes = buildThemesWithScores(updatedItems);
    autoSavedRef.current = true;
    autoSavePromiseRef.current = waitForAutoSave().then(() =>
      patchThemes(newThemes).catch(() => {
        /* silent — localStorage backup still intact */
      })
    );
  };

  // Puts the server back to the themes the session started with. Returns an
  // error message, or null when nothing needed restoring or it succeeded.
  const restoreSnapshot = async () => {
    await waitForAutoSave();
    const snapshot = snapshotRef.current;
    if (!autoSavedRef.current || !profileId) return null;
    if (!snapshot || snapshot.profileId !== profileId) return null;
    try {
      await patchThemes(snapshot.themes);
      autoSavedRef.current = false;
      return null;
    } catch (err) {
      return errorText(err);
    }
  };

  const handleScore = (score) => {
    if (phaseRef.current !== 'assess') return;
    const idx = indexRef.current;
    const current = itemsRef.current;
    if (!current[idx]) return;
    const newItems = [...current];
    // `scored` marks cards scored in this session (highlights the chosen button)
    newItems[idx] = { ...newItems[idx], score, scored: true };
    storeState(profileId, idx + 1, selectedThemes, newItems);
    updateItems(newItems);

    // Track streak
    if (score === 2) {
      setStreak((s) => {
        const n = s + 1;
        setMaxStreak((m) => Math.max(m, n));
        return n;
      });
    } else {
      setStreak(0);
    }

    // Periodic server auto-save every AUTO_SAVE_EVERY items
    autoSaveCountRef.current += 1;
    if (autoSaveCountRef.current >= AUTO_SAVE_EVERY) {
      autoSaveCountRef.current = 0;
      serverAutoSave(newItems);
    }

    // Auto-advance
    if (idx < newItems.length - 1) {
      goToIndex(idx + 1);
    } else {
      changePhase('summary');
    }
  };

  const handleBack = () => {
    if (phaseRef.current !== 'assess') return;
    if (indexRef.current > 0) goToIndex(indexRef.current - 1);
  };

  const handleSkip = () => {
    if (phaseRef.current !== 'assess') return;
    if (indexRef.current < itemsRef.current.length - 1) {
      goToIndex(indexRef.current + 1);
    } else {
      changePhase('summary');
    }
  };

  keyHandlersRef.current = { handleScore, handleBack, handleSkip };

  // Keyboard support
  useEffect(() => {
    if (!open || phase !== 'assess') return;
    const handleKeyDown = (e) => {
      // Held keys auto-repeat — one physical press scores one card
      if (e.repeat || e.ctrlKey || e.metaKey || e.altKey) return;
      if (closeConfirmOpen || cheatSheetOpen || saving) return;
      // Typing a clip title or using the recorder dialog must not score cards
      if (recPanelOpenRef.current) return;
      const keys = keyHandlersRef.current;
      if (e.key === 'ArrowLeft' || e.key === '0') keys.handleScore(0);
      if (e.key === 'ArrowDown' || e.key === '1') {
        e.preventDefault();
        keys.handleScore(1);
      }
      if (e.key === 'ArrowRight' || e.key === '2') keys.handleScore(2);
      if (e.key === 'Backspace') keys.handleBack();
      if (e.key === ' ') {
        e.preventDefault();
        keys.handleSkip();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, phase, closeConfirmOpen, cheatSheetOpen, saving]);

  const hasUnsavedScores = () =>
    (phase === 'assess' || phase === 'summary') &&
    items.some((i) => i.score !== i.originalScore);

  // opts.endSession (bulk only): save this student, then end the session
  const handleSaveAndExit = async (opts = {}) => {
    if (!profileReady || saving) return;
    if (blockedByRecording()) return;
    if (
      (!bulkSession || bulkSession.isLast || opts.endSession) &&
      blockedByUnsentClips()
    )
      return;
    const newThemes = buildThemesWithScores(items);
    setActionError(null);

    // Save to DB — on failure keep the dialog and the localStorage backup
    if (profileId) {
      setSaving(true);
      try {
        await waitForAutoSave();
        await patchThemes(newThemes);
        autoSavedRef.current = false;
      } catch (err) {
        const reason = errorText(err);
        setActionError({
          action: 'save',
          opts,
          message:
            language === 'en'
              ? `${reason}. Scores were not saved, but are kept on this device.`
              : `${reason}. Оценки не сохранены, но остались на этом устройстве.`,
        });
        return;
      } finally {
        setSaving(false);
      }
    }

    if (live.active) {
      setSaving(true);
      await live.finish();
      setSaving(false);
    }
    clearStoredState();
    if (onSave) onSave(newThemes, opts);
    // In a bulk session the parent moves on to the next student
    if (!bulkSession) onClose();
  };

  // Discard this session: put back the scores the session started with
  // (auto-save may already have written some), then close
  const discardAndClose = async ({ confirm = false } = {}) => {
    if (
      confirm &&
      !window.confirm(
        language === 'en'
          ? 'Discard the changes from this session? Scores will go back to what they were.'
          : 'Отменить изменения этой сессии? Оценки вернутся к прежним.'
      )
    )
      return;
    setCloseConfirmOpen(false);
    setActionError(null);
    setSaving(true);
    await live.discard();
    const error = await restoreSnapshot();
    setSaving(false);
    if (error) {
      setActionError({
        action: 'discard',
        message:
          language === 'en'
            ? `Could not restore the previous scores: ${error}`
            : `Не удалось вернуть прежние оценки: ${error}`,
      });
      return;
    }
    clearStoredState();
    onClose();
  };

  // Bulk only: student is absent — move on without saving anything for them
  const handleSkipStudent = async ({ confirm = true } = {}) => {
    if (saving) return;
    if (blockedByRecording()) return;
    if (
      confirm &&
      hasUnsavedScores() &&
      !window.confirm(
        language === 'en'
          ? "Skip this student? This session's scores for them will not be saved."
          : 'Пропустить ученика? Оценки этой сессии для него не сохранятся.'
      )
    )
      return;
    setActionError(null);
    setSaving(true);
    await live.discard();
    const error = await restoreSnapshot();
    setSaving(false);
    if (error) {
      setActionError({
        action: 'skip',
        message:
          language === 'en'
            ? `Could not undo this student's auto-saved scores: ${error}`
            : `Не удалось отменить автосохранённые оценки ученика: ${error}`,
      });
      return;
    }
    clearStoredState();
    if (onSkipStudent) onSkipStudent();
  };

  const retryFailedAction = () => {
    if (!actionError) return;
    const { action, opts } = actionError;
    if (action === 'save') handleSaveAndExit(opts);
    else if (action === 'discard') discardAndClose();
    else if (action === 'skip') handleSkipStudent({ confirm: false });
  };

  // Show confirm dialog if there are unsaved scores (or, in a bulk session,
  // always — closing ends the session); otherwise close immediately
  const handleRequestClose = () => {
    if (saving) return;
    if (blockedByRecording() || blockedByUnsentClips()) return;
    if (bulkSession || hasUnsavedScores()) {
      setCloseConfirmOpen(true);
      return;
    }
    discardAndClose();
  };

  // Swipe gesture handlers (left=0, right=2, up=1)
  const handleTouchStart = (e) => {
    const t = e.touches[0];
    touchStartRef.current = { x: t.clientX, y: t.clientY };
  };

  const handleTouchEnd = (e) => {
    if (!touchStartRef.current || phase !== 'assess') return;
    const dx = e.changedTouches[0].clientX - touchStartRef.current.x;
    const dy = e.changedTouches[0].clientY - touchStartRef.current.y;
    touchStartRef.current = null;
    const MIN = 60;
    if (Math.abs(dx) >= MIN && Math.abs(dx) > Math.abs(dy)) {
      handleScore(dx > 0 ? 2 : 0);
    } else if (dy < -MIN && Math.abs(dy) > Math.abs(dx)) {
      handleScore(1);
    }
  };

  // Summary stats
  const getSummaryStats = () => {
    const mastered = items.filter((i) => i.score === 2).length;
    const developing = items.filter((i) => i.score === 1).length;
    const needsWork = items.filter((i) => i.score === 0).length;

    // Per-theme breakdown
    const themeStats = {};
    items.forEach((item) => {
      if (!themeStats[item.themeName])
        themeStats[item.themeName] = {
          mastered: 0,
          developing: 0,
          needsWork: 0,
          total: 0,
        };
      themeStats[item.themeName].total++;
      if (item.score === 2) themeStats[item.themeName].mastered++;
      else if (item.score === 1) themeStats[item.themeName].developing++;
      else themeStats[item.themeName].needsWork++;
    });

    return { mastered, developing, needsWork, themeStats };
  };

  const activeItem = items[currentIndex];
  const currentThemeName = activeItem?.themeName;
  const uniqueThemes = [...new Set(items.map((i) => i.themeName))];
  const currentThemeNum = uniqueThemes.indexOf(currentThemeName) + 1;
  const selectedItemCount = profileReady
    ? profile.themes
        .filter((t) => selectedThemes.includes(t.themeName))
        .reduce((s, t) => s + (t.items || []).length, 0)
    : 0;
  const unsavedForDialog = hasUnsavedScores();

  // Same order and wording as the profile page: 0 · 1 · 2, left → right
  const scoreOptions = [
    {
      score: 0,
      color: 'error',
      label: language === 'en' ? "Doesn't know" : 'Не знает',
      hint: '← · 0',
    },
    {
      score: 1,
      color: 'warning',
      label: language === 'en' ? 'Partly' : 'Частично',
      hint: '↓ · 1',
    },
    {
      score: 2,
      color: 'success',
      label: language === 'en' ? 'Knows' : 'Знает',
      hint: '→ · 2',
    },
  ];

  let saveLabel;
  let saveLabelShort;
  if (!bulkSession) {
    saveLabel = language === 'en' ? 'Save & Exit' : 'Сохранить и выйти';
    saveLabelShort = language === 'en' ? 'Save' : 'Сохранить';
  } else if (bulkSession.isLast) {
    saveLabel = language === 'en' ? 'Save & finish' : 'Сохранить и завершить';
    saveLabelShort = language === 'en' ? 'Finish' : 'Завершить';
  } else {
    saveLabel = language === 'en' ? 'Save & next →' : 'Сохранить и далее →';
    saveLabelShort = language === 'en' ? 'Next →' : 'Далее →';
  }

  const bulkStatusLabel = (status) =>
    ({
      done: language === 'en' ? 'assessed' : 'оценён',
      skipped: language === 'en' ? 'skipped' : 'пропущен',
      current: language === 'en' ? 'now' : 'сейчас',
      pending: language === 'en' ? 'waiting' : 'ожидает',
    })[status] || '';
  const bulkStudents = bulkSession?.students || [];
  const bulkSkippedCount = bulkStudents.filter(
    (s) => s.status === 'skipped'
  ).length;

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={handleRequestClose}
      TransitionComponent={Transition}
    >
      <AppBar sx={{ position: 'relative', bgcolor: '#111827' }}>
        <Toolbar
          sx={{ minHeight: { xs: 52, sm: 60 }, px: { xs: 1, sm: 2 }, gap: 1 }}
        >
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleRequestClose}
            aria-label="close"
            sx={{ ml: 0 }}
          >
            <CloseIcon />
          </IconButton>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              noWrap
              sx={{
                fontWeight: 700,
                lineHeight: 1.25,
                fontSize: { xs: '1rem', sm: '1.15rem' },
              }}
            >
              {bulkSession
                ? language === 'en'
                  ? `Student ${(bulkSession.index ?? 0) + 1} of ${bulkSession.total}: ${bulkSession.studentName || ''}`
                  : `Ученик ${(bulkSession.index ?? 0) + 1} из ${bulkSession.total}: ${bulkSession.studentName || ''}`
                : language === 'en'
                  ? 'Speed Assessment'
                  : 'Быстрая оценка'}
            </Typography>
            {!bulkSession && sessionTitle && (
              <Typography
                variant="caption"
                noWrap
                sx={{ color: 'rgba(255,255,255,0.75)', display: 'block' }}
              >
                {sessionTitle}
              </Typography>
            )}
          </Box>
          {clipQueue.clips.length > 0 && (
            <Chip
              size="small"
              clickable
              onClick={() => setClipsDialogOpen(true)}
              color={clipsFailed ? 'error' : 'default'}
              label={`${language === 'en' ? 'Video' : 'Видео'} ${
                sessionClips.filter((c) => c.status === 'done').length
              }/${sessionClips.length}`}
              sx={{
                color: '#fff',
                bgcolor: clipsFailed ? undefined : 'rgba(255,255,255,0.18)',
                fontWeight: 700,
              }}
            />
          )}
          {canLive &&
            (phase === 'assess' ||
              live.active ||
              live.status === 'saved' ||
              live.status === 'error') && (
              <LiveRecordingPill
                live={live}
                en={language === 'en'}
                enabled={autoRecord}
                onEnable={enableLive}
                onStop={stopLiveNow}
              />
            )}
          {!live.active && (
            <AssessmentVideoRecorder
              disabled={!profileReady || saving}
              onBusyChange={handleRecBusy}
              onPanelOpenChange={handleRecPanel}
              onOpen={() => {
                recProfileRef.current = profileId || null;
              }}
              onClip={(clip) =>
                clipQueue.add({ ...clip, profileId: recProfileRef.current })
              }
              floatingBottom={{
                xs: 'calc(150px + env(safe-area-inset-bottom))',
                sm: 176,
              }}
            />
          )}
          <IconButton
            color="inherit"
            onClick={() => setCheatSheetOpen(true)}
            aria-label="keyboard shortcuts"
            sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
          >
            <HelpOutlineIcon />
          </IconButton>
          <Button
            color="inherit"
            onClick={() => handleSaveAndExit()}
            variant="outlined"
            sx={{
              borderRadius: 4,
              flexShrink: 0,
              whiteSpace: 'nowrap',
              textTransform: 'none',
              fontWeight: 700,
            }}
            startIcon={saving ? null : <SaveIcon />}
            disabled={saving || !profileReady}
          >
            {saving ? (
              '...'
            ) : (
              <>
                <Box
                  component="span"
                  sx={{ display: { xs: 'none', sm: 'inline' } }}
                >
                  {bulkSession
                    ? saveLabel
                    : language === 'en'
                      ? 'Save & Exit'
                      : 'Сохранить'}
                </Box>
                <Box
                  component="span"
                  sx={{ display: { xs: 'inline', sm: 'none' } }}
                >
                  {saveLabelShort}
                </Box>
              </>
            )}
          </Button>
        </Toolbar>

        {/* Bulk: who is done / skipped / next, and the one-tap "absent" */}
        {bulkSession && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: { xs: 1.5, sm: 2.5 },
              pb: 1,
            }}
          >
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}
            >
              {bulkStudents.map((s, i) => (
                <Tooltip
                  key={i}
                  title={`${s.name || '?'} — ${bulkStatusLabel(s.status)}`}
                >
                  <Box
                    data-status={s.status}
                    sx={{
                      flexShrink: 0,
                      width: s.status === 'current' ? 12 : 8,
                      height: s.status === 'current' ? 12 : 8,
                      borderRadius: '50%',
                      bgcolor:
                        s.status === 'done'
                          ? '#22c55e'
                          : s.status === 'current'
                            ? '#fff'
                            : s.status === 'pending'
                              ? 'rgba(255,255,255,0.25)'
                              : 'transparent',
                      border:
                        s.status === 'skipped'
                          ? '1.5px solid rgba(255,255,255,0.55)'
                          : 'none',
                      boxShadow:
                        s.status === 'current'
                          ? '0 0 0 3px rgba(255,255,255,0.25)'
                          : 'none',
                    }}
                  />
                </Tooltip>
              ))}
              <Typography
                variant="caption"
                noWrap
                sx={{
                  pl: 1,
                  color: 'rgba(255,255,255,0.7)',
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                {language === 'en'
                  ? `${bulkSession.assessed} assessed · ${bulkSkippedCount} skipped`
                  : `оценено ${bulkSession.assessed} · пропущено ${bulkSkippedCount}`}
              </Typography>
            </Stack>
            <Button
              color="inherit"
              size="small"
              variant="outlined"
              onClick={() => handleSkipStudent()}
              startIcon={<PersonOffIcon />}
              disabled={saving}
              aria-label={
                language === 'en'
                  ? 'Skip (not in class)'
                  : 'Пропустить (нет на уроке)'
              }
              sx={{
                flexShrink: 0,
                borderRadius: 4,
                borderColor: 'rgba(255,255,255,0.35)',
                textTransform: 'none',
                whiteSpace: 'nowrap',
                py: 0.25,
              }}
            >
              <Box
                component="span"
                sx={{ display: { xs: 'none', sm: 'inline' } }}
              >
                {language === 'en'
                  ? 'Skip (not in class)'
                  : 'Пропустить (нет на уроке)'}
              </Box>
              <Box
                component="span"
                sx={{ display: { xs: 'inline', sm: 'none' } }}
              >
                {language === 'en' ? 'Not in class' : 'Нет на уроке'}
              </Box>
            </Button>
          </Box>
        )}
      </AppBar>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          bgcolor: '#f3f4f6',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {videoWarn && (
          <Alert
            severity="warning"
            onClose={() => setVideoWarn('')}
            sx={{ borderRadius: 0, py: 0.25, flexShrink: 0 }}
          >
            {videoWarn}
          </Alert>
        )}
        {actionError && (
          <Alert
            severity="error"
            sx={{ borderRadius: 0, py: 0.25, flexShrink: 0 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={retryFailedAction}
                disabled={saving}
                sx={{ fontWeight: 700 }}
              >
                {language === 'en' ? 'Retry' : 'Повторить'}
              </Button>
            }
          >
            {actionError.message}
          </Alert>
        )}

        {/* Profile missing or malformed — never crash, explain instead */}
        {!profileReady && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 3,
            }}
          >
            <Paper
              sx={{
                p: 4,
                maxWidth: 500,
                width: '100%',
                borderRadius: 4,
                textAlign: 'center',
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                {language === 'en'
                  ? 'Could not load this assessment'
                  : 'Не удалось загрузить оценку'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {language === 'en'
                  ? 'The assessment has no themes or did not load. Try opening it again.'
                  : 'В оценке нет тем, или она не загрузилась. Попробуйте открыть её заново.'}
              </Typography>
              {bulkSession ? (
                <Button
                  variant="contained"
                  startIcon={<PersonOffIcon />}
                  onClick={() => handleSkipStudent({ confirm: false })}
                  disabled={saving}
                  sx={{ borderRadius: 3 }}
                >
                  {language === 'en'
                    ? 'Skip this student'
                    : 'Пропустить ученика'}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={() => onClose()}
                  sx={{ borderRadius: 3 }}
                >
                  {language === 'en' ? 'Close' : 'Закрыть'}
                </Button>
              )}
            </Paper>
          </Box>
        )}

        {/* Phase: Theme Selection */}
        {phase === 'select' && profileReady && (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              p: { xs: 2, sm: 3 },
            }}
          >
            <Paper
              sx={{
                p: { xs: 3, sm: 4 },
                my: 'auto',
                maxWidth: 500,
                width: '100%',
                borderRadius: 4,
              }}
            >
              <Typography
                variant="h5"
                sx={{ fontWeight: 800, mb: 3, textAlign: 'center' }}
              >
                {language === 'en' ? 'Select Themes' : 'Выберите темы'}
              </Typography>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedThemes.length === profile.themes.length}
                      onChange={(e) =>
                        setSelectedThemes(
                          e.target.checked
                            ? profile.themes.map((t) => t.themeName)
                            : []
                        )
                      }
                    />
                  }
                  label={
                    <Typography sx={{ fontWeight: 700 }}>
                      {language === 'en' ? 'All Themes' : 'Все темы'}
                    </Typography>
                  }
                />
                <Divider sx={{ my: 1 }} />
                {profile.themes.map((theme, idx) => (
                  <FormControlLabel
                    key={idx}
                    control={
                      <Checkbox
                        checked={selectedThemes.includes(theme.themeName)}
                        onChange={(e) =>
                          setSelectedThemes((prev) =>
                            e.target.checked
                              ? [...prev, theme.themeName]
                              : prev.filter((n) => n !== theme.themeName)
                          )
                        }
                      />
                    }
                    label={`${theme.themeName} (${(theme.items || []).length} ${language === 'en' ? 'items' : 'эл.'})`}
                  />
                ))}
              </FormGroup>
              {canLive && (
                <Box
                  sx={{
                    mt: 2,
                    p: 1.25,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <FormControlLabel
                    sx={{ m: 0, width: '100%' }}
                    control={
                      <Switch
                        checked={autoRecord}
                        onChange={(e) => setAutoRecord(e.target.checked)}
                      />
                    }
                    label={
                      <Box>
                        <Typography
                          sx={{ fontWeight: 700, fontSize: '0.95rem' }}
                        >
                          {language === 'en'
                            ? 'Record the assessment on video'
                            : 'Записывать проверку на видео'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {language === 'en'
                            ? 'Starts with the first card, stops at the end and is saved to this assessment.'
                            : 'Начнётся с первой карточки, закончится в конце и сохранится в эту оценку.'}
                        </Typography>
                      </Box>
                    }
                  />
                  {autoRecord && (
                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      value={camFacing}
                      onChange={(e, v) => v && setCamFacing(v)}
                      sx={{ mt: 1, ml: 5.5 }}
                    >
                      <ToggleButton value="user">
                        {language === 'en' ? 'Front camera' : 'Передняя камера'}
                      </ToggleButton>
                      <ToggleButton value="environment">
                        {language === 'en' ? 'Back camera' : 'Задняя камера'}
                      </ToggleButton>
                    </ToggleButtonGroup>
                  )}
                </Box>
              )}
              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={startAssessment}
                disabled={
                  selectedThemes.length === 0 || selectedItemCount === 0
                }
                sx={{ mt: 3, borderRadius: 3, py: 1.5, fontWeight: 700 }}
              >
                {language === 'en'
                  ? `Start (${selectedItemCount} items)`
                  : `Начать (${selectedItemCount} эл.)`}
              </Button>
            </Paper>
          </Box>
        )}

        {/* Assessment with nothing to assess */}
        {phase === 'assess' && profileReady && items.length === 0 && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 3,
            }}
          >
            <Paper
              sx={{
                p: 4,
                maxWidth: 500,
                width: '100%',
                borderRadius: 4,
                textAlign: 'center',
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                {language === 'en'
                  ? 'There are no words or phrases to assess here'
                  : 'Здесь нет слов и фраз для оценки'}
              </Typography>
              {bulkSession ? (
                <Button
                  variant="contained"
                  startIcon={<PersonOffIcon />}
                  onClick={() => handleSkipStudent({ confirm: false })}
                  disabled={saving}
                  sx={{ borderRadius: 3 }}
                >
                  {language === 'en'
                    ? 'Skip this student'
                    : 'Пропустить ученика'}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={() => onClose()}
                  sx={{ borderRadius: 3 }}
                >
                  {language === 'en' ? 'Close' : 'Закрыть'}
                </Button>
              )}
            </Paper>
          </Box>
        )}

        {/* Phase: Assessment — card fills what's left, buttons always visible */}
        {phase === 'assess' && profileReady && activeItem && (
          <>
            <LinearProgress
              variant="determinate"
              value={((currentIndex + 1) / items.length) * 100}
              sx={{ height: 4, flexShrink: 0 }}
            />

            {/* Current theme + position */}
            <Box
              sx={{
                px: { xs: 2, sm: 3 },
                pt: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexShrink: 0,
              }}
            >
              <Typography
                variant="body2"
                noWrap
                sx={{
                  flex: 1,
                  minWidth: 0,
                  fontWeight: 700,
                  color: 'text.secondary',
                }}
              >
                {currentThemeName}
                {uniqueThemes.length > 1 && (
                  <Box component="span" sx={{ fontWeight: 500, opacity: 0.7 }}>
                    {' · '}
                    {language === 'en' ? 'theme' : 'тема'} {currentThemeNum}/
                    {uniqueThemes.length}
                  </Box>
                )}
              </Typography>
              <StreakBadge streak={streak} en={language === 'en'} />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 800,
                  color: 'text.primary',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {currentIndex + 1} / {items.length}
              </Typography>
            </Box>

            {filterFallback && (
              <Alert
                severity="info"
                sx={{
                  mx: { xs: 2, sm: 3 },
                  mt: 1,
                  py: 0,
                  fontSize: '0.8rem',
                  flexShrink: 0,
                }}
              >
                {language === 'en'
                  ? 'The selected themes are not in this assessment — showing all of its themes.'
                  : 'Выбранных тем нет в этой оценке — показаны все её темы.'}
              </Alert>
            )}

            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: { xs: 2, sm: 3 },
                py: { xs: 1, sm: 2 },
                position: 'relative',
              }}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              {/* correct answers in a row: sparks, confetti, Черри cheering */}
              <StreakFx streak={streak} en={language === 'en'} />
              {(() => {
                const baseKey = activeItem.itemText?.toLowerCase().trim();
                const meaning = (activeItem.meaning || '').toLowerCase().trim();
                const imgUrl =
                  (meaning && wordImages[`${baseKey}|${meaning}`]) ||
                  wordImages[baseKey];
                return (
                  <Card
                    sx={{
                      width: '100%',
                      maxWidth: 700,
                      height: '100%',
                      maxHeight: 520,
                      borderRadius: 4,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      overflow: 'hidden',
                      boxShadow:
                        '0 10px 25px -8px rgba(0,0,0,0.12), 0 4px 10px -4px rgba(0,0,0,0.05)',
                    }}
                  >
                    <CardContent
                      sx={{
                        textAlign: 'center',
                        width: '100%',
                        minHeight: 0,
                        py: { xs: 2, sm: 3 },
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                      }}
                    >
                      <Chip
                        label={
                          activeItem.itemType === 'phrase'
                            ? language === 'en'
                              ? 'PHRASE'
                              : 'Фраза'
                            : language === 'en'
                              ? 'WORD'
                              : 'Слово'
                        }
                        size="small"
                        variant="outlined"
                        color={
                          activeItem.itemType === 'phrase'
                            ? 'secondary'
                            : 'primary'
                        }
                        sx={{ mb: 1.5, height: 22 }}
                      />
                      {imgUrl && (
                        <Box
                          component="img"
                          src={imgUrl}
                          alt={activeItem.itemText}
                          sx={{
                            maxHeight: { xs: '20vh', sm: '30vh' },
                            maxWidth: '90%',
                            mb: 1.5,
                            borderRadius: 3,
                            objectFit: 'contain',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                          }}
                        />
                      )}
                      <Typography
                        variant="h2"
                        sx={{
                          fontWeight: 900,
                          color: '#1f2937',
                          lineHeight: 1.1,
                          wordBreak: 'break-word',
                          fontSize: {
                            xs: imgUrl ? '1.9rem' : '2.4rem',
                            sm: imgUrl ? '2.6rem' : '3.4rem',
                          },
                        }}
                      >
                        {activeItem.itemText}
                      </Typography>
                      {activeItem.meaning && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 1 }}
                        >
                          {activeItem.meaning}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                );
              })()}
            </Box>

            <Box
              sx={{
                flexShrink: 0,
                bgcolor: 'white',
                borderTop: '1px solid #e5e7eb',
                px: { xs: 1.5, sm: 3 },
                pt: 0.75,
                pb: {
                  xs: 'calc(12px + env(safe-area-inset-bottom))',
                  sm: 2,
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  maxWidth: 760,
                  mx: 'auto',
                  mb: 0.75,
                }}
              >
                <Button
                  size="small"
                  startIcon={<ArrowBackIcon />}
                  onClick={handleBack}
                  disabled={currentIndex === 0}
                  sx={{ fontWeight: 700, textTransform: 'none' }}
                >
                  {language === 'en' ? 'Back' : 'Назад'}
                </Button>
                <Button
                  size="small"
                  color="inherit"
                  endIcon={<SkipNextIcon />}
                  onClick={handleSkip}
                  sx={{ color: 'text.secondary', textTransform: 'none' }}
                >
                  {language === 'en' ? 'Skip' : 'Пропустить'}
                </Button>
              </Box>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: { xs: 1, sm: 2 },
                  maxWidth: 760,
                  mx: 'auto',
                }}
              >
                {scoreOptions.map((opt) => {
                  const chosen =
                    activeItem.scored && activeItem.score === opt.score;
                  return (
                    <Button
                      key={opt.score}
                      variant="contained"
                      color={opt.color}
                      disableElevation
                      aria-pressed={chosen}
                      aria-label={`${opt.label} (${opt.score})`}
                      onClick={() => handleScore(opt.score)}
                      sx={{
                        height: { xs: 72, sm: 92 },
                        borderRadius: 3,
                        flexDirection: 'column',
                        gap: 0.25,
                        px: 0.5,
                        textTransform: 'none',
                        outline: chosen ? '3px solid #111827' : 'none',
                        outlineOffset: 2,
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          fontSize: { xs: '1rem', sm: '1.2rem' },
                          fontWeight: 800,
                          lineHeight: 1.15,
                        }}
                      >
                        {opt.label}
                      </Box>
                      <Box
                        component="span"
                        sx={{
                          fontSize: '0.72rem',
                          fontWeight: 600,
                          opacity: 0.85,
                          letterSpacing: 0.5,
                        }}
                      >
                        {opt.hint}
                      </Box>
                    </Button>
                  );
                })}
              </Box>
            </Box>
          </>
        )}

        {/* Phase: Summary — compact; actions pinned to the bottom */}
        {phase === 'summary' &&
          profileReady &&
          (() => {
            const { mastered, developing, needsWork, themeStats } =
              getSummaryStats();
            const counts = { 0: needsWork, 1: developing, 2: mastered };
            const tint = { 0: '#fef2f2', 1: '#fffbeb', 2: '#ecfdf5' };
            const ink = { 0: '#dc2626', 1: '#d97706', 2: '#059669' };
            return (
              <>
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflow: 'auto',
                    p: { xs: 2, sm: 3 },
                  }}
                >
                  <Paper
                    sx={{
                      p: { xs: 2.5, sm: 4 },
                      maxWidth: 600,
                      mx: 'auto',
                      borderRadius: 4,
                    }}
                  >
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: 800, textAlign: 'center', mb: 0.5 }}
                    >
                      {language === 'en'
                        ? 'Assessment complete'
                        : 'Оценка завершена'}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ textAlign: 'center', mb: 2.5 }}
                    >
                      {bulkSession?.studentName
                        ? `${bulkSession.studentName} · `
                        : ''}
                      {items.length} {language === 'en' ? 'items' : 'эл.'}
                      {maxStreak >= 3 &&
                        (language === 'en'
                          ? ` · best streak ${maxStreak}`
                          : ` · лучшая серия ${maxStreak} подряд`)}
                    </Typography>

                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: 1,
                        mb: 3,
                      }}
                    >
                      {scoreOptions.map((opt) => (
                        <Box
                          key={opt.score}
                          sx={{
                            p: 1.25,
                            textAlign: 'center',
                            bgcolor: tint[opt.score],
                            borderRadius: 3,
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 800,
                              fontSize: '1.8rem',
                              lineHeight: 1.1,
                              color: ink[opt.score],
                            }}
                          >
                            {counts[opt.score]}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 700 }}
                          >
                            {opt.label}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    {Object.entries(themeStats).map(([name, stats]) => {
                      const pct =
                        stats.total > 0
                          ? Math.round((stats.mastered / stats.total) * 100)
                          : 0;
                      return (
                        <Box key={name} sx={{ mb: 1.5 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              gap: 1,
                              mb: 0.5,
                            }}
                          >
                            <Typography
                              variant="body2"
                              noWrap
                              sx={{ fontWeight: 600, minWidth: 0 }}
                            >
                              {name}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                color:
                                  pct >= 80
                                    ? 'success.main'
                                    : pct >= 50
                                      ? 'warning.main'
                                      : 'error.main',
                              }}
                            >
                              {pct}%
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={pct}
                            color={
                              pct >= 80
                                ? 'success'
                                : pct >= 50
                                  ? 'warning'
                                  : 'error'
                            }
                            sx={{ height: 6, borderRadius: 3 }}
                          />
                        </Box>
                      );
                    })}

                    {sessionClips.length > 0 && (
                      <Box sx={{ mt: 2.5 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 800, mb: 1 }}
                        >
                          {language === 'en' ? 'Video' : 'Видео'}
                          {` · ${
                            sessionClips.filter((c) => c.status === 'done')
                              .length
                          }/${sessionClips.length} ${
                            language === 'en' ? 'uploaded' : 'загружено'
                          }`}
                        </Typography>
                        <PendingClipsPanel
                          clips={sessionClips}
                          onRetry={clipQueue.retry}
                          onDiscard={clipQueue.discard}
                        />
                      </Box>
                    )}
                  </Paper>
                </Box>

                <Box
                  sx={{
                    flexShrink: 0,
                    bgcolor: 'white',
                    borderTop: '1px solid #e5e7eb',
                    px: { xs: 1.5, sm: 3 },
                    pt: 1.5,
                    pb: {
                      xs: 'calc(12px + env(safe-area-inset-bottom))',
                      sm: 2,
                    },
                  }}
                >
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    sx={{ maxWidth: 600, mx: 'auto' }}
                  >
                    {needsWork > 0 && (
                      <Button
                        variant="outlined"
                        color="error"
                        sx={{
                          borderRadius: 3,
                          flex: 1,
                          fontWeight: 700,
                          textTransform: 'none',
                          whiteSpace: 'nowrap',
                        }}
                        onClick={() => {
                          // Jump back to first needs-work item
                          const idx = items.findIndex((i) => i.score === 0);
                          if (idx >= 0) {
                            goToIndex(idx);
                            changePhase('assess');
                          }
                        }}
                      >
                        {language === 'en'
                          ? `Re-check (${needsWork})`
                          : `Перепроверить (${needsWork})`}
                      </Button>
                    )}
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => handleSaveAndExit()}
                      disabled={saving}
                      sx={{
                        borderRadius: 3,
                        flex: 1.4,
                        fontWeight: 700,
                        textTransform: 'none',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {saving
                        ? language === 'en'
                          ? 'Saving...'
                          : 'Сохранение...'
                        : saveLabel}
                    </Button>
                  </Stack>
                </Box>
              </>
            );
          })()}
      </Box>

      {/* Confirm close with unsaved scores / end of a bulk session */}
      <Dialog
        open={closeConfirmOpen}
        onClose={() => setCloseConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {bulkSession
            ? language === 'en'
              ? 'End the session?'
              : 'Завершить сессию?'
            : language === 'en'
              ? 'Unsaved Scores'
              : 'Несохранённые оценки'}
        </DialogTitle>
        <DialogContent>
          {bulkSession && (
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, mb: unsavedForDialog ? 1 : 0 }}
            >
              {language === 'en'
                ? `Assessed ${bulkSession.assessed} of ${bulkSession.total}`
                : `Оценено ${bulkSession.assessed} из ${bulkSession.total}`}
            </Typography>
          )}
          {(unsavedForDialog || !bulkSession) && (
            <Typography variant="body2" color="text.secondary">
              {bulkSession
                ? language === 'en'
                  ? "This student's scores are not saved yet."
                  : 'Оценки текущего ученика ещё не сохранены.'
                : language === 'en'
                  ? 'You have scored items that have not been saved yet. What would you like to do?'
                  : 'Вы оценили элементы, которые ещё не сохранены. Что сделать?'}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1, flexWrap: 'wrap' }}>
          <Button onClick={() => setCloseConfirmOpen(false)} size="small">
            {language === 'en' ? 'Keep Assessing' : 'Продолжить'}
          </Button>
          {(unsavedForDialog || !bulkSession) && (
            <Button
              color="error"
              onClick={() => discardAndClose({ confirm: !bulkSession })}
              disabled={saving}
              size="small"
            >
              {bulkSession
                ? language === 'en'
                  ? "Don't save & end"
                  : 'Не сохранять и завершить'
                : language === 'en'
                  ? 'Discard'
                  : 'Отменить'}
            </Button>
          )}
          {unsavedForDialog || !bulkSession ? (
            <Button
              variant="contained"
              onClick={() => {
                setCloseConfirmOpen(false);
                handleSaveAndExit(bulkSession ? { endSession: true } : {});
              }}
              disabled={saving || !profileReady}
              size="small"
            >
              {bulkSession
                ? language === 'en'
                  ? 'Save & end'
                  : 'Сохранить и завершить'
                : language === 'en'
                  ? 'Save & Exit'
                  : 'Сохранить'}
            </Button>
          ) : (
            <Button
              variant="contained"
              color="error"
              onClick={() => discardAndClose()}
              disabled={saving}
              size="small"
            >
              {language === 'en' ? 'End session' : 'Завершить'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Video clips: upload status / retry */}
      <Dialog
        open={clipsDialogOpen}
        onClose={() => setClipsDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {language === 'en' ? 'Video clips' : 'Видеоклипы'}
        </DialogTitle>
        <DialogContent>
          <PendingClipsPanel
            clips={sessionClips}
            onRetry={clipQueue.retry}
            onDiscard={clipQueue.discard}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClipsDialogOpen(false)}>
            {language === 'en' ? 'Close' : 'Закрыть'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Keyboard & Swipe Cheat Sheet */}
      <Dialog
        open={cheatSheetOpen}
        onClose={() => setCheatSheetOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {language === 'en' ? 'Shortcuts & Gestures' : 'Быстрые клавиши'}
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            {language === 'en' ? 'Keyboard' : 'Клавиатура'}
          </Typography>
          <List dense disablePadding>
            {[
              ['←  /  0', `${scoreOptions[0].label} (0)`],
              ['↓  /  1', `${scoreOptions[1].label} (1)`],
              ['→  /  2', `${scoreOptions[2].label} (2)`],
              ['Space', language === 'en' ? 'Skip' : 'Пропустить'],
              ['Backspace', language === 'en' ? 'Go back' : 'Назад'],
            ].map(([key, desc]) => (
              <ListItem key={key} sx={{ py: 0.5 }}>
                <ListItemText
                  primary={
                    <Chip
                      label={key}
                      size="small"
                      variant="outlined"
                      sx={{ fontFamily: 'monospace', fontWeight: 700 }}
                    />
                  }
                  secondary={desc}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    '& .MuiListItemText-secondary': { ml: 1 },
                  }}
                />
              </ListItem>
            ))}
          </List>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            {language === 'en' ? 'Touch / Swipe' : 'Жесты'}
          </Typography>
          <List dense disablePadding>
            {[
              [
                language === 'en' ? '← Swipe left' : '← Свайп влево',
                `${scoreOptions[0].label} (0)`,
              ],
              [
                language === 'en' ? '↑ Swipe up' : '↑ Свайп вверх',
                `${scoreOptions[1].label} (1)`,
              ],
              [
                language === 'en' ? '→ Swipe right' : '→ Свайп вправо',
                `${scoreOptions[2].label} (2)`,
              ],
            ].map(([key, desc]) => (
              <ListItem key={key} sx={{ py: 0.5 }}>
                <ListItemText
                  primary={
                    <Chip
                      label={key}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ fontWeight: 700 }}
                    />
                  }
                  secondary={desc}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    '& .MuiListItemText-secondary': { ml: 1 },
                  }}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCheatSheetOpen(false)}>
            {language === 'en' ? 'Got it' : 'Понятно'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
